import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';

function CameraDragList({ cameras, onReorder, onRemove }) {
  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) {
      return;
    }

    const reordered = Array.from(cameras);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);

    // Update positions
    const withPositions = reordered.map((camera, index) => ({
      ...camera,
      position: index,
    }));

    onReorder(withPositions);
  };

  if (cameras.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <VideocamIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No cameras added yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click "Add Cameras" to select cameras for this set
        </Typography>
      </Paper>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="camera-list">
        {(droppableProvided) => (
          <List
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            sx={{ p: 0 }}
          >
            {cameras.map((camera, index) => (
              <Draggable
                key={`${camera.site_id}-${camera.camera_id}`}
                draggableId={`${camera.site_id}-${camera.camera_id}`}
                index={index}
              >
                {(draggableProvided, snapshot) => (
                  <ListItem
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    sx={{
                      mb: 1,
                      bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                      border: 1,
                      borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      '&:last-child': { mb: 0 },
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onRemove(camera)}
                        sx={{ mr: -1 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon
                      {...draggableProvided.dragHandleProps}
                      sx={{ minWidth: 36, cursor: 'grab' }}
                    >
                      <DragIndicatorIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={camera.display_name || camera.name || `Camera ${camera.camera_id}`}
                      secondary={camera.site_display_name || camera.site_name || `Site ${camera.site_id}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default CameraDragList;
